import * as React from "react";
import { Box, CPHrBreak, CPText } from "shared/components/base";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPActionsheetModal from "shared/components/shared/CPActionsheetModal/CPActionsheetModal";
import CPResourceSecurityHelpModal from "shared/components/app/CPResourceSecurityHelpModal/CPResourceSecurityHelpModal";
import CPHelpButton from "shared/components/shared/CPHelpButton/CPHelpButton";
import CPTextBox from "shared/components/shared/CPTextBox/CPTextBox";
import CPTextArea from "shared/components/shared/CPTextArea/CPTextArea";
import CPCheckbox from "shared/components/shared/CPCheckbox";
import CPDropDownList from "shared/components/shared/CPDropDownList/CPDropDownList";
import useAppState from "shared/state/useAppState";
import { ICON_OPTIONS } from "shared/constants";
import {
  useGetCategoriesForResource,
  useGetRolesForResource,
} from "data/resources/resource";
import CPListBox, {
  CPBoxOption,
} from "shared/components/shared/CPListBox/CPListBox";
import { PageResource, useUpsertPageMutation } from "data/resources/page";
import { ResourceCategory, ResourceRole } from "data/resources/resource";
import CPSpacer from "shared/components/shared/CPSpacer/CPSpacer";

export class UpsertPageRecord implements Omit<PageResource, "agencies"> {
  id: number = -1;
  title: string = "";
  url: string = "";
  iconName?: string = "";
  mode?: "NONE" | "WINDOW" | "TAB" = "NONE";
  isHidden?: boolean = false;
  categories: string[] = [];
  roles?: string[] = [];
  summary?: string = "";
  resourceTier?: string = "";
  isActive: boolean = true;
}

interface PageDetailsModalProps {
  selectedPage: UpsertPageRecord | null;
  onClose: () => void;
  onSave: () => void;
}

const iconItemRender = (li: React.ReactElement, itemProps: any) => {
  const { dataItem } = itemProps;
  return React.cloneElement(
    li,
    li.props,
    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <i
        className={dataItem.value}
        style={{ width: 16, textAlign: "center" }}
      />
      <span>{dataItem.label}</span>
    </span>,
  );
};

const iconValueRender = (element: React.ReactElement, value: any) => {
  if (!value) return element;
  return React.cloneElement(
    element,
    element.props,
    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <i className={value.value} style={{ width: 16, textAlign: "center" }} />
      <span>{value.label}</span>
    </span>,
  );
};

const PageDetailsModal: React.FC<PageDetailsModalProps> = ({
  selectedPage,
  onClose,
  onSave,
}) => {
  const { setToastMsg, setIsLoading } = useAppState();
  const { data: categories, isLoading: categoriesLoading } =
    useGetCategoriesForResource(selectedPage?.id ?? -1);
  const { data: roles, isLoading: rolesLoading } = useGetRolesForResource(
    selectedPage?.id ?? -1,
  );
  const {
    mutate,
    isLoading: isSaving,
    error: saveError,
  } = useUpsertPageMutation();

  const [editPage, setEditPage] = React.useState<UpsertPageRecord | null>({
    ...selectedPage,
  } as UpsertPageRecord);

  const [categoryOptions, setCategoryOptions] = React.useState<CPBoxOption>({
    available: [],
    selected: [],
  });

  const [roleOptions, setRoleOptions] = React.useState<CPBoxOption>({
    available: [],
    selected: [],
  });

  const [showSecurityHelp, setShowSecurityHelp] = React.useState(false);

  React.useEffect(() => {
    setIsLoading(categoriesLoading || rolesLoading || isSaving);
  }, [categoriesLoading, rolesLoading, isSaving, setIsLoading]);

  React.useEffect(() => {
    if (categories) {
      setCategoryOptions({
        available: categories.available.map((c: ResourceCategory) => ({
          value: c.name,
          label: c.parentName ? `${c.name} (${c.parentName})` : c.name,
          selected: false,
        })),
        selected: categories.assigned.map((c: ResourceCategory) => ({
          value: c.name,
          label: c.parentName ? `${c.name} (${c.parentName})` : c.name,
          selected: false,
        })),
      });
    }
  }, [categories]);

  React.useEffect(() => {
    if (roles) {
      setRoleOptions({
        available: roles.available.map((r: ResourceRole) => ({
          value: r.name,
          label: r.name,
          selected: false,
        })),
        selected: roles.assigned.map((r: ResourceRole) => ({
          value: r.name,
          label: r.name,
          selected: false,
        })),
      });
    }
  }, [roles]);

  const handleCategoryChange = React.useCallback(
    (options: CPBoxOption) => {
      if (!categories) {
        setCategoryOptions(options);
        return;
      }

      const allCategories = [...categories.available, ...categories.assigned];

      const getAncestorIds = (name: string): string[] => {
        const cat = allCategories.find((c) => c.name === name);
        if (!cat?.parentName) return [];
        const parent = allCategories.find((c) => c.name === cat.parentName);
        if (!parent) return [];
        return [parent.name, ...getAncestorIds(parent.name)];
      };

      const selectedIds = new Set(options.selected.map((o) => o.value));
      const ancestorIds = new Set(
        options.selected.flatMap((o) => getAncestorIds(o.value as string)),
      );

      const autoPromoted = options.available
        .filter(
          (o) =>
            ancestorIds.has(o.value as string) &&
            !selectedIds.has(o.value as string),
        )
        .map((o) => ({ ...o, selected: false }));

      const allRequiredIds = new Set(
        Array.from(selectedIds).concat(Array.from(ancestorIds)),
      );

      setCategoryOptions({
        available: options.available.filter(
          (o) => !allRequiredIds.has(o.value as string),
        ),
        selected: [...options.selected, ...autoPromoted],
      });
    },
    [categories],
  );

  const handleSave = () => {
    if (!editPage) return;
    mutate(
      {
        ...editPage,
        categories: categoryOptions.selected.map((c) => c.value as string),
        roles: roleOptions.selected.map((r) => r.value as string),
      },
      {
        onSuccess: () => {
          setToastMsg("Page saved successfully", "success");
          onSave();
        },
        onError: (error: unknown) => {
          if ((error as Record<string, unknown>)["errors"]) {
            setToastMsg(
              "Please check the form for errors and try again",
              "error",
            );
            return;
          }
          setToastMsg("An error occurred during the request.", "error");
        },
      },
    );
  };

  const isNew = selectedPage?.id === -1;

  return (
    <CPActionsheetModal
      title={isNew ? "Add Page" : "Edit Page"}
      open={selectedPage !== null && !rolesLoading && !categoriesLoading}
      onClose={onClose}
      position="right"
      width={550}
      actionBtns={
        <Box spacing={8}>
          <CPButton themeColor="primary" onClick={handleSave}>
            Save
          </CPButton>
        </Box>
      }
    >
      {selectedPage && (
        <Box spacing={24} flexDirection="column" style={{ paddingBottom: 16 }}>
          <Box spacing={12} flexDirection="column">
            <Box className="cp-actionsheet-input-box">
              <CPTextBox
                toCapital
                required
                name="title"
                label="Page Title"
                value={editPage?.title}
                error={saveError}
                onChange={(e) => {
                  setEditPage(
                    (prev) =>
                      ({
                        ...prev,
                        title: e.target.value,
                      }) as UpsertPageRecord,
                  );
                }}
              />
            </Box>
            <Box className="cp-actionsheet-input-box">
              <CPTextBox
                required
                name="url"
                label="Page URL"
                error={saveError}
                helpText="Enter a fully qualified URL (e.g. https://example.com/page) or a root-relative path (e.g. /some-page)."
                value={editPage?.url}
                onChange={(e) => {
                  setEditPage(
                    (prev) =>
                      ({
                        ...prev,
                        url: e.target.value,
                      }) as UpsertPageRecord,
                  );
                }}
              />
            </Box>

            <Box className="cp-actionsheet-input-box">
              <CPDropDownList
                name="iconName"
                label="Menu Icon"
                textField="label"
                dataItemKey="value"
                valueKey="value"
                defaultItem={{ label: "None", value: "" }}
                data={ICON_OPTIONS}
                value={editPage?.iconName ?? ""}
                itemRender={iconItemRender}
                valueRender={iconValueRender}
                onChange={(e) => {
                  setEditPage(
                    (prev) =>
                      ({
                        ...prev,
                        iconName: e.value as string,
                      }) as UpsertPageRecord,
                  );
                }}
              />
            </Box>
            <Box className="cp-actionsheet-input-box">
              <CPDropDownList
                name="mode"
                label="View Mode"
                textField="label"
                dataItemKey="value"
                valueKey="value"
                defaultValue={"NONE"}
                data={[
                  { label: "Same window", value: "NONE" },
                  { label: "New window", value: "WINDOW" },
                  { label: "New tab", value: "TAB" },
                ]}
                value={editPage?.mode ?? "NONE"}
                onChange={(e) => {
                  setEditPage(
                    (prev) =>
                      ({
                        ...prev,
                        mode: e.value,
                      }) as UpsertPageRecord,
                  );
                }}
              />
            </Box>
            <Box className="cp-actionsheet-input-box">
              <CPCheckbox
                name="isActive"
                label="Active"
                checked={editPage?.isActive}
                onChange={(e) => {
                  setEditPage(
                    (prev) =>
                      ({
                        ...prev,
                        isActive: e.value,
                      }) as UpsertPageRecord,
                  );
                }}
              />
              <CPText>Check to allow this page</CPText>
            </Box>
            <Box className="cp-actionsheet-input-box">
              <CPCheckbox
                name="isHidden"
                label="Hide on menu"
                checked={editPage?.isHidden ?? false}
                onChange={(e) => {
                  setEditPage(
                    (prev) =>
                      ({
                        ...prev,
                        isHidden: e.value,
                      }) as UpsertPageRecord,
                  );
                }}
              />
              <CPText>Check to hide this page from navigation menus</CPText>
            </Box>
            <Box className="cp-actionsheet-input-box">
              <CPTextArea
                name="summary"
                label="Page Summary"
                error={saveError}
                disableResize
                value={editPage?.summary}
                rows={4}
                onChange={(e) => {
                  setEditPage(
                    (prev) =>
                      ({
                        ...prev,
                        summary: e.value,
                      }) as UpsertPageRecord,
                  );
                }}
              />
            </Box>
          </Box>

          <Box spacing={12} flexDirection="column">
            <Box className="cp-actionsheet-input-box">
              <Box
                flexDirection="row"
                alignItems="center"
                justifyContent="flex-start"
                spacing={10}
              >
                <Box flexDirection="column" spacing={4}>
                  <CPText
                    textStyle="h6"
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <i
                      className="fa-solid fa-lock"
                      style={{ marginRight: 8 }}
                    />
                    Access & Security
                  </CPText>
                  <CPText style={{ opacity: 0.7 }}>
                    Control who can access this resource
                  </CPText>
                </Box>
                <CPHelpButton
                  onClick={() => setShowSecurityHelp(true)}
                  title="How does security work?"
                  aria-label="Visibility help"
                />
              </Box>
              <CPSpacer height={15} />
              <Box>
                <CPText textStyle="subtitle" style={{ fontSize: 13 }}>
                  Resource Tier
                </CPText>
                <CPText
                  textStyle="caption"
                  style={{ opacity: 0.8, fontSize: 12, marginTop: 4 }}
                >
                  Lock the page to agencies with matching tier access.
                </CPText>
              </Box>

              <CPSpacer height={8} />

              <CPDropDownList
                name="resourceTier"
                label="Tier"
                textField="label"
                dataItemKey="value"
                valueKey="value"
                defaultItem={{ label: "Please choose...", value: null }}
                data={[
                  { label: "Bronze", value: "BRONZE" },
                  { label: "Silver", value: "SILVER" },
                  { label: "Gold", value: "GOLD" },
                ]}
                value={editPage?.resourceTier ?? null}
                onChange={(e) => {
                  setEditPage(
                    (prev) =>
                      ({
                        ...prev,
                        resourceTier: e.value,
                      }) as UpsertPageRecord,
                  );
                }}
              />
            </Box>
            <Box className="cp-actionsheet-input-box">
              <CPListBox
                name="categories"
                headerText="Assigned Categories *"
                filterable
                boxOptions={categoryOptions}
                onChange={handleCategoryChange}
                error={saveError}
              />
            </Box>
            <Box className="cp-actionsheet-input-box">
              <CPListBox
                name="roles"
                headerText="Assigned Roles"
                filterable
                boxOptions={roleOptions}
                onChange={(options) => setRoleOptions(options)}
              />
              v
            </Box>
          </Box>
        </Box>
      )}

      <CPResourceSecurityHelpModal
        open={showSecurityHelp}
        onClose={() => setShowSecurityHelp(false)}
      />
    </CPActionsheetModal>
  );
};

export default PageDetailsModal;
